import {type EventMap, Listenable, type Notification} from "@juis/commons";
import {RestError} from "@juis/model";
import {client} from "./backend.js";

export interface UserDto {
    readonly id: number;
    readonly email: string;
    readonly displayName: string | null;
    readonly avatarUrl: string | null;
    readonly emailVerified: boolean;
}

interface SessionEvents extends EventMap {
    change: Notification<{ user: UserDto | null }>;
}

export class LoginFailedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "LoginFailedError";
    }
}

export class RegistrationFailedError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "RegistrationFailedError";
    }
}

const toUser = (payload: unknown): UserDto | null => {
    if (typeof payload !== "object" || payload === null) {
        return null;
    }
    const candidate = payload as {
        id?: unknown;
        email?: unknown;
        displayName?: unknown;
        avatarUrl?: unknown;
        emailVerified?: unknown;
    };
    if (typeof candidate.id !== "number" || typeof candidate.email !== "string") {
        return null;
    }
    return {
        id: candidate.id,
        email: candidate.email,
        displayName: typeof candidate.displayName === "string" ? candidate.displayName : null,
        avatarUrl: typeof candidate.avatarUrl === "string" ? candidate.avatarUrl : null,
        emailVerified: candidate.emailVerified === true
    };
};

/** The message the backend put in an error payload, if it phrased one. */
const errorMessage = (error: unknown, fallback: string): string => {
    if (error instanceof RestError) {
        const body = error.body;
        if (typeof body === "object" && body !== null && !Array.isArray(body)) {
            const message = body["error"];
            if (typeof message === "string" && message !== "") {
                return message;
            }
        }
    }
    return fallback;
};

/**
 * Holds who is signed in. `/api/auth/session` answers 401 for anonymous callers, so an absent
 * user is a normal answer here rather than an error.
 */
export class Session extends Listenable<SessionEvents> {
    #user: UserDto | null = null;
    #loaded = false;

    get user(): UserDto | null {
        return this.#user;
    }

    get displayName(): string {
        const user = this.#user;
        if (user === null) {
            return "";
        }
        return user.displayName ?? user.email;
    }

    async load(): Promise<UserDto | null> {
        if (!this.#loaded) {
            this.#loaded = true;
            try {
                this.#set(toUser(await client.getOne("/auth/session")));
            } catch (error) {
                if (!(error instanceof RestError) || error.status !== 401) {
                    // A transport or server failure is not an answer about who is signed in.
                    this.#loaded = false;
                }
                this.#set(null);
            }
        }
        return this.#user;
    }

    async login(email: string, password: string): Promise<UserDto> {
        try {
            await client.post("/auth/login", {username: email, password});
        } catch (error) {
            throw new LoginFailedError(error instanceof RestError && error.status === 401
                ? "Wrong email or password"
                : errorMessage(error, "Could not sign in right now"));
        }
        this.#loaded = false;
        const user = await this.load();
        if (user === null) {
            throw new LoginFailedError("Signed in, but the session could not be read");
        }
        return user;
    }

    async register(email: string, password: string, displayName: string): Promise<UserDto> {
        try {
            await client.post("/auth/register", {email, password, displayName});
        } catch (error) {
            throw new RegistrationFailedError(errorMessage(error, "Could not create the account"));
        }
        return this.login(email, password);
    }

    async logout(): Promise<void> {
        try {
            await client.post("/auth/logout", {});
        } finally {
            this.#loaded = true;
            this.#set(null);
        }
    }

    #set(user: UserDto | null): void {
        this.#user = user;
        this.trigger("change", {user});
    }
}

export const session = new Session();
